import { useEffect } from 'react';
import './App.css';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { routes } from './routes';
import { usePageTrackingGA, useWebgate } from '@xvia/webgate-connect';
import {
  ServicesFiltersContextProvider,
  useServicesFilters,
} from './hooks/use-services-filters';
import { QueryClientProvider } from '@tanstack/react-query';
import { defaultQueryClient } from './lib/query-client';
import { useServiceWorker } from './hooks/use-service-worker';

declare global {
  interface Window {
    VLibras: {
      Widget: new (url: string) => void;
    };
  }
}

function AppContent() {
  const location = useLocation();
  const history = useHistory();
  const { portalMessages, userInfo } = useWebgate();
  const { setFilters } = useServicesFilters();

  useServiceWorker();

  usePageTrackingGA(location);

  useEffect(() => {
    const headers = document.querySelectorAll('header');
    if (headers.length > 1) {
      headers.forEach((header, index) => {
        if (index > 0) {
          if (!header.getAttribute('role')) {
            header.setAttribute('role', 'presentation');
          }
        }
      });
    }

    const mainElement = document.querySelector('main');
    if (mainElement) {
      const parent = mainElement.parentElement;
      if (
        parent &&
        (parent.tagName === 'HEADER' ||
          parent.tagName === 'FOOTER' ||
          parent.tagName === 'NAV' ||
          parent.tagName === 'ASIDE')
      ) {
        console.warn(
          'Main element is nested inside a semantic element:',
          parent.tagName
        );
      }
    }
  }, [location]);

  useEffect(() => {
    if (portalMessages?.detail?.pushRoute) {
      const route = portalMessages.detail.pushRoute;
      const searchMatch = route.match(/\/search\/(.+)/);

      if (searchMatch && searchMatch[1]) {
        const searchTerm = decodeURIComponent(searchMatch[1]);

        setFilters((prev) => ({
          ...prev,
          searchQuery: searchTerm,
          currentPage: 1,
        }));
      }

      history.push(route);
    }
  }, [history, portalMessages, setFilters]);

  useEffect(() => {
    const intelliframe = document.getElementById(
      'intelliframe'
    ) as HTMLIFrameElement;

    if (userInfo) {
      intelliframe?.contentWindow?.postMessage(
        { type: 'USER_INFO_RESPONSE', payload: userInfo },
        '*'
      );
    }
  }, [userInfo]);

  return (
    <main id="main-content">
      <Switch>
        {routes.map(({ component: RouteComponent, path, exact }) => (
          <Route key={path} path={path} exact={exact}>
            <RouteComponent />
          </Route>
        ))}
      </Switch>
    </main>
  );
}

function App() {
  const isSSR = typeof window === 'undefined';

  if (isSSR) {
    return (
      <Switch>
        {routes.map(({ component: RouteComponent, path, exact }) => (
          <Route key={path} path={path} exact={exact}>
            <RouteComponent />
          </Route>
        ))}
      </Switch>
    );
  }

  return (
    <QueryClientProvider client={defaultQueryClient}>
      <ServicesFiltersContextProvider>
        <AppContent />
      </ServicesFiltersContextProvider>
    </QueryClientProvider>
  );
}

export default App;
